import React from 'react';
import type {LucideIcon} from 'lucide-react';
import {
    BoldIcon,
    ItalicIcon,
    TextAlignCenterIcon,
    TextAlignEndIcon,
    TextAlignStartIcon
} from 'lucide-react';
import CanvasController from './CanvasController.ts';
import {FontOptions, TextAlign} from './types.ts';
import {ToolButton, Toolbar, ToolbarDivider} from '../ds/ToolFrame.tsx';

interface TextToolbarProps {
    controlsDisabled: boolean;
    controller: CanvasController;
}

const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72];

const textAlignOptions: {
    title: string;
    align: TextAlign;
    icon: LucideIcon;
}[] = [
    {title: 'Align left', align: 'left', icon: TextAlignStartIcon},
    {title: 'Align centre', align: 'center', icon: TextAlignCenterIcon},
    {title: 'Align right', align: 'right', icon: TextAlignEndIcon}
];

const TextToolbar: React.FC<TextToolbarProps> = ({
    controlsDisabled,
    controller
}) => {
    const fontSettings =
        controller.activeObjectFontSettings() ?? controller.fontSettings;

    return (
        <Toolbar label="Text" className="whiteboard-toolbar">
            <ToolButton
                label="Bold"
                icon={BoldIcon}
                selected={fontSettings.bold}
                onClick={() => controller.setFontOptions(FontOptions.Bold)}
                disabled={controlsDisabled}
            />
            <ToolButton
                label="Italic"
                icon={ItalicIcon}
                selected={fontSettings.italic}
                onClick={() => controller.setFontOptions(FontOptions.Italic)}
                disabled={controlsDisabled}
            />
            <ToolbarDivider />
            {textAlignOptions.map(option => (
                <ToolButton
                    key={option.align}
                    label={option.title}
                    icon={option.icon}
                    selected={(fontSettings.align ?? 'left') === option.align}
                    onClick={() => controller.setTextAlign(option.align)}
                    disabled={controlsDisabled}
                />
            ))}
            <ToolbarDivider />
            <select
                className="form-select form-select-sm mx-toolbar-select"
                aria-label="Font"
                value={fontSettings.font}
                onChange={e => controller.onFontChange(e.target.value)}
            >
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Quicksand">Quicksand</option>
            </select>
            <select
                className="form-select form-select-sm mx-toolbar-select mx-toolbar-select-narrow"
                aria-label="Font size"
                value={fontSettings.fontSize ?? ''}
                onChange={e => {
                    const fontSize = e.target.value
                        ? parseInt(e.target.value)
                        : undefined;
                    controller.setFontSize(fontSize);
                }}
                disabled={controlsDisabled}
            >
                <option value="">Size</option>
                {fontSizes.map(size => (
                    <option key={size} value={size}>
                        {size}
                    </option>
                ))}
            </select>
        </Toolbar>
    );
};

export default TextToolbar;
